import type { BoxProps } from '@mui/material/Box';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { useQuery } from '@tanstack/react-query';
import {
  Tab as MUITab,
  Tabs as MUITabs,
} from 'components/ui/common/Tabs/index.js';
import { get } from 'es-toolkit/compat';
import { useArraySearchParams } from 'features/units/hooks/useArraySearchParams.js';
import type {
  CSSProperties,
  FC,
  ReactElement,
  ReactNode,
} from 'react';
import type React from 'react';
import {
  Children,
  useEffect,
} from 'react';
import { useAccountUnitContext } from 'shared/context/useAccountUnitsContext.js';
import { useMyUser } from 'shared/context/useMyUser.js';

import { ActionNeededBadge } from './Tabs.styled.js';
import {
  badgeNeededCheckers,
  renderCheckers,
  useConditionalQueryParam,
} from './Tabs.utils.js';

export type TabPanelProps = {
  value: number | string;
} & BoxProps;

export const Tabs = ({
  tabs = [],
  children = [],
  searchParam,
  style,
  defaultTab,
  onChange,
}: {
  tabs: {
    label: ReactNode;
    value: number | string;
    badgeNeededChecker?: string;
    renderChecker?: keyof typeof renderCheckers;
  }[];
  /** Children can be a callbck as well */
  children: ReactElement<{ value: string }>[] | ((payload: { goToTab: (tab: string) => void }) => ReactElement<{ value: string }>[]);
  searchParam?: string;
  style?: CSSProperties;
  defaultTab?: number | string;
  onChange?: VoidFunction;
}) => {
  const { account } = useAccountUnitContext();
  const [currentTab, setCurrentTab] = useConditionalQueryParam({
    searchParam,
    defaultValue: defaultTab ?? tabs[0].value,
  });

  useEffect(() => {
    if (!account?.type) return;
    const renderChecker = get(
      tabs.find(tab => tab.value === currentTab),
      'renderChecker',
    );
    // reset to first available tab if current tab is not renderable
    if (renderChecker && !renderCheckers[renderChecker]?.(account.type)) {
      setCurrentTab(
        tabs.filter(tab => !tab.renderChecker)[0].value as string,
      );
    }
  }, [account?.type]);

  const handleChange = (newValue: string) => {
    setCurrentTab(newValue);
    onChange?.();
  };

  const childrenNodes = typeof children === 'function' ? children({ goToTab: handleChange }) : children;

  return (
    <Box sx={{ width: '100%' }}>
      <Box>
        <MUITabs
          variant="scrollable"
          value={currentTab}
          onChange={(_, newValue) => handleChange(newValue)}
          style={style}
        >
          {tabs
            .filter(({ renderChecker }) => {
              if (!renderChecker) {
                return true;
              }
              return renderCheckers[renderChecker]?.(account?.type);
            })
            .map(({ label, value, badgeNeededChecker }) => (
              <Tab
                key={`${value}-tab`}
                value={value}
                label={label}
                badgeNeededChecker={badgeNeededChecker}
              />
            ))}
        </MUITabs>
      </Box>
      <Divider sx={{ mb: 1.25 }} />
      {Children.map(childrenNodes, (child) => {
        if (
          child.props.value === currentTab
        ) {
          return child;
        }
        return null;
      })}
    </Box>
  );
};

export const TabPanel = ({ children, ...boxProps }: TabPanelProps) => {
  return <Box {...boxProps}>{children}</Box>;
};

const Tab: FC<{
  value: string | number;
  label: ReactNode;
  badgeNeededChecker?: string;
}> = ({ value, label, badgeNeededChecker, ...props }) => {
  const [accountIds] = useArraySearchParams('accountIds');
  const { user } = useMyUser();

  const { data: badgeNeeded = false } = useQuery({
    queryKey: [`badgeNeededChecker.${value}`, accountIds[0]],
    queryFn: () =>
      badgeNeededCheckers[badgeNeededChecker!]({
        ownerId: accountIds[0],
        isRegistryAdmin: Boolean(user?.isRegistryAdmin),
        currentUserId: user?.id ?? '',
      }),
    enabled: Boolean(badgeNeededChecker) && Boolean(user?.id),
  });

  if (badgeNeeded) {
    return (
      <ActionNeededBadge color="error" variant="dot">
        <MUITab key={value} label={label} value={value} {...props} />
      </ActionNeededBadge>
    );
  }

  return <MUITab key={value} label={label} value={value} {...props} />;
};
